// FILE: frontend/src/components/LoginPage.tsx
// COMPLETE VERSION - Registration removed for Admin/Manager

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

type UserRole = 'admin' | 'manager' | 'boss';

interface LoginPageProps {
  onSwitchToRegister?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onSwitchToRegister }) => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<UserRole>('admin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const BACKEND_URL = 'https://api.askfinancee.com'; // Define the backend URL

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let endpoint = '';
      let tokenKey = '';
      let userKey = '';
      let redirectPath = '';

      switch (selectedRole) {
        case 'admin':
          endpoint = '/api/admin/login';
          tokenKey = 'token'; // Kept original key name
          userKey = 'admin';
          redirectPath = '/dashboard';
          break;
        case 'manager':
          endpoint = '/api/manager/login';
          tokenKey = 'managerToken';
          userKey = 'manager';
          redirectPath = '/manager/dashboard';
          break;
        case 'boss':
          endpoint = '/api/boss/login';
          tokenKey = 'bossToken';
          userKey = 'boss';
          redirectPath = '/boss/dashboard';
          break;
      }

      // Use the BACKEND_URL variable directly
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || 'Login failed');
      }

      localStorage.setItem(tokenKey, data.token);
      localStorage.setItem(userKey, JSON.stringify(data[selectedRole] || data.user || data.admin));

      // Force a reload to ensure the app's auth state is updated from localStorage
      window.location.assign(redirectPath);

    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const roleConfig = {
    admin: {
      title: 'Employee Portal',
      icon: '👨‍💼',
      description: 'Create and manage leads',
      gradient: 'from-blue-500 to-cyan-500'
    },
    manager: {
      title: 'Manager Portal',
      icon: '📊',
      description: 'Review leads and coordinate with banks',
      gradient: 'from-indigo-500 to-purple-500'
    },
    boss: {
      title: 'Boss Portal',
      icon: '👑',
      description: 'Manage team and oversee operations',
      gradient: 'from-purple-600 to-pink-600'
    }
  };

  const currentRole = roleConfig[selectedRole];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-5xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Loan Leads CRM</h1>
          <p className="text-gray-600">Select your role to continue</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Role Selection Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Select Your Role</h2>

            <div className="space-y-4">
              {/* Admin Role */}
              <button
                onClick={() => setSelectedRole('admin')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  selectedRole === 'admin'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-4xl">{roleConfig.admin.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-lg">
                      {roleConfig.admin.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {roleConfig.admin.description}
                    </p>
                  </div>
                  {selectedRole === 'admin' && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>

              {/* Manager Role */}
              <button
                onClick={() => setSelectedRole('manager')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  selectedRole === 'manager'
                    ? 'border-indigo-500 bg-indigo-50 shadow-md'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-4xl">{roleConfig.manager.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-lg">
                      {roleConfig.manager.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {roleConfig.manager.description}
                    </p>
                  </div>
                  {selectedRole === 'manager' && (
                    <div className="w-6 h-6 bg-indigo-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>

              {/* Boss Role */}
              <button
                onClick={() => setSelectedRole('boss')}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  selectedRole === 'boss'
                    ? 'border-purple-500 bg-purple-50 shadow-md'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-4xl">{roleConfig.boss.icon}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800 text-lg">
                      {roleConfig.boss.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {roleConfig.boss.description}
                    </p>
                  </div>
                  {selectedRole === 'boss' && (
                    <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            </div>
          </div>

          {/* Login Form Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-8">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${currentRole.gradient} text-white text-3xl mb-4`}>
                {currentRole.icon}
              </div>
              <h2 className="text-2xl font-bold text-gray-800">{currentRole.title}</h2>
              <p className="text-gray-600 mt-1">{currentRole.description}</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                  placeholder={`${selectedRole}@example.com`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a10.007 10.007 0 011.664-3.582M9 9l3 3m0 0l3 3m-3-3l-3 3m3-3l3-3M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.522 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.478 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-gradient-to-r ${currentRole.gradient} text-white py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {loading ? 'Signing in...' : `Sign in as ${selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}`}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 text-center">
                {selectedRole === 'boss' ? (
                  <>
                    First time here?{' '}
                    <button
                      onClick={onSwitchToRegister}
                      className="text-purple-600 hover:text-purple-800 font-semibold"
                    >
                      Register Boss Account
                    </button>
                  </>
                ) : selectedRole === 'manager' ? (
                  'Manager accounts are created by Boss. Contact your administrator.'
                ) : (
                  'Employee accounts are created by Boss. Contact your administrator.'
                )}
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default LoginPage;